import React from "react";
import { useForm, Validate } from "react-hook-form";

interface FormInputProps extends FormTextAreaProps {
  type?: string;
}
interface FormSelectProps extends FormFieldProps {
  options: { text: string; value: string }[];
}

interface FormToggleProps extends FormFieldProps {
  checked: boolean;
  setChecked: React.Dispatch<React.SetStateAction<boolean>>;
  defaultValue?: boolean;
}

interface FormTextAreaProps extends FormFieldProps {
  tall?: boolean;
  min?: number;
  max?: number;
  defaultValue?: string;
}
interface FormFieldProps {
  readOnly?: boolean;
  label: string;
  name: string;
  note?: string;
  required?: boolean;
  placeholder?: string;
  pattern?: RegExp;
  validate?: Validate | Record<string, Validate>;
  register: ReturnType<typeof useForm>["register"];
  errors: ReturnType<typeof useForm>["errors"];
}
export const FormLabel = ({
  label,
  htmlFor,
}: {
  label: string;
  htmlFor: string;
}) => (
  <label className="block text-sm font-bold mb-2" htmlFor={htmlFor}>
    {label}
  </label>
);

export const FormInput = ({
  label,
  name,
  note,
  type = "text",
  required,
  min,
  max,
  pattern,
  validate,
  placeholder,
  readOnly,
  register,
  errors,
  defaultValue,
}: FormInputProps) => (
  <>
    <FormLabel htmlFor={name} label={label} />
    <input
      type={type}
      name={name}
      id={name}
      ref={register({ required, min, max, pattern, validate })}
      disabled={readOnly}
      readOnly={readOnly}
      defaultValue={defaultValue}
      className={`
      ${
        errors[name]
          ? "border-red-500"
          : "border-gray-200 focus:border-gray-500"
      }
      ${note || errors[name] ? "mb-3 " : ""}
      appearance-none block w-full bg-gray-200 border rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white`}
      placeholder={placeholder}
    />
    {note && <p className="text-gray-600 text-xs italic">{note}</p>}

    {errors[name] && errors[name]?.type === "required" && (
      <p className="text-red-500 text-xs italic">{label} is required</p>
    )}
    {errors[name] && errors[name]?.type === "pattern" && (
      <p className="text-red-500 text-xs italic">not valid</p>
    )}
  </>
);

export const FormToggle = ({
  label,
  name,
  note,
  checked,
  setChecked,
  register,
  errors,
  defaultValue,
}: FormToggleProps) => (
  <>
    <FormLabel htmlFor={name} label={label} />
    <label
      htmlFor={name}
      title={name}
      className="mt-3 w-56 flex items-center cursor-pointer mr-2"
    >
      <span className="relative">
        <span
          className={`${
            checked ? "bg-green-400" : "bg-gray-400"
          } block w-10 h-6 rounded-full`}
        ></span>
        <span
          className={`${
            checked ? "translate-x-full" : ""
          } absolute block w-4 h-4 mt-1 ml-1 rounded-full inset-y-0 left-0 focus-within:shadow-outline transition-transform duration-300 ease-in-out bg-white transform`}
        >
          <input
            id={name}
            name={name}
            type="checkbox"
            ref={register()}
            className="absolute opacity-0 w-0 h-0"
            checked={checked}
            defaultChecked={checked}
            onChange={(e) => {
              setChecked(e.target.checked);
            }}
          />
          {errors[name] && errors[name]?.type === "required" && (
            <p className="text-red-500 text-xs italic">{label} is required</p>
          )}
        </span>
      </span>
      <span className="ml-3 text-sm">{note}</span>
    </label>
  </>
);

export const FormSelect = ({
  label,
  name,
  options,
  note,
  required,
  placeholder,
  register,
  errors,
}: FormSelectProps) => (
  <>
    <FormLabel htmlFor={name} label={label} />
    <select
      name={name}
      id={name}
      ref={register({ required })}
      className="block appearance-none bg-white border border-gray-400 w-full hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline"
    >
      <option value="">{placeholder}</option>
      {options.map(({ text, value }, index) => (
        <option key={index} value={value}>
          {text}
        </option>
      ))}
    </select>
    {note && <p className="text-gray-600 text-xs italic">{note}</p>}
    {errors[name] && errors[name]?.type === "required" && (
      <p className="text-red-500 text-xs italic">{label} is required</p>
    )}
  </>
);

export const FormTextArea = ({
  label,
  name,
  note,
  required,
  min,
  max,
  validate,
  placeholder,
  register,
  errors,
  tall = false,
}: FormTextAreaProps) => (
  // TODO: this should be merged with FormInput, as is basically identical
  <>
    <FormLabel htmlFor={name} label={label} />
    <textarea
      name={name}
      id={name}
      ref={register({ required, min, max, validate })}
      className={`
      ${tall ? "h-56" : "h-24"}
      ${
        errors[name]
          ? "border-red-500"
          : "border-gray-200 focus:border-gray-500"
      }
      ${note || errors[name] ? "mb-3 " : ""}  
      appearance-none block w-full bg-gray-200 border rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white focus:border-gray-500`}
      placeholder={placeholder}
    />
    {note && <p className="text-gray-600 text-xs italic">{note}</p>}
    {errors[name] && errors[name]?.type === "required" && (
      <p className="text-red-500 text-xs italic">{label} is required</p>
    )}
  </>
);

export const FormButtonRow = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center justify-between">{children}</div>
);
export const FormSubmit = ({
  isLoading,
  label,
}: {
  isLoading: boolean;
  label: string;
}) => (
  <input
    type="submit"
    disabled={isLoading}
    value={label}
    className={`${
      isLoading
        ? "cursor-not-allowed bg-blue-200"
        : "cursor-pointer bg-blue-500 hover:bg-blue-700"
    } text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline`}
  />
);

export const FormMultiFieldRow = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="flex flex-wrap -mx-3 mb-6">
    {children &&
      React.Children.map(children, (child) => (
        <div className="w-full md:w-1/3 px-3 mb-6 md:mb-0">{child}</div>
      ))}
  </div>
);

export const FormSingleFieldRow = ({
  children,
}: {
  children: React.ReactNode;
}) => (
  <div className="flex flex-wrap -mx-3 mb-6">
    <div className="w-full px-3">{children}</div>
  </div>
);

export const Form = ({
  onSubmit,
  serverError,
  children,
  fullWidth = false,
}: {
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  serverError?: string;
  fullWidth?: boolean;
  children: React.ReactNode;
}) => (
  <form
    className={`w-full${fullWidth ? "" : " max-w-lg"}`}
    noValidate
    onSubmit={onSubmit}
  >
    {serverError && <p className="text-red-500 text-xs mb-4">{serverError}</p>}
    {children}
  </form>
);
